import { requireTab } from "@/features/navigation/requireTab";
import ErrorPage from "@/components/pages/ErrorPage";
import { diariesBySettingsQueryOptions } from "@/features/diaries/api";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { selectPatientsSettingsByIdQueryOptions } from "@/features/patients/api/useSelectPatientsSettings";
import PatientsPage from "@/features/patients/components/PatientsPage";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { DEFAULT_PATIENT_PAGE_SIZE, parsePatientPageSize } from "@/features/patients/patientPageSize";
import { hooks as h } from "@/hooks";
import { createFileRoute } from "@tanstack/react-router";
import type { StateKeyT } from "@patient-watch/types";
import { z } from "zod";

const diaryStatusKeys = [
    "NEVER_WORKED",
    "HIGH",
    "PAIN_SPIKE",
    "NORMAL",
    "PAIN_REDUCED",
    "LOW",
    "EMPTY_DIARY",
    "COMPLETED",
    "DISCARDED",
    "PENDING",
    "ACTIVE",
    "LATE_TO_LOG",
    "LATE_TO_START",
    "NO_TRACKER",
    "READY_TO_REVIEW",
    "LOW_PAIN",
    "MODERATE_PAIN",
    "HIGH_PAIN",
] as const satisfies readonly StateKeyT[];

export const DiaryStatusZ = z.enum(diaryStatusKeys);

const PatientsErrorPage = ({ error, reset }: { error: Error; reset: () => void }) => {
    const copy = usePatientsCopy();
    return <ErrorPage title={copy.patientsError} error={error} reset={reset} />;
};

const PatientsPageContent = () => {
    const copy = usePatientsCopy();
    h.useNav([{ label: copy.patients, route: { to: "/patients" } }]);

    const { settings } = Route.useLoaderData();

    return <PatientsPage settings={settings} />;
};

export const StyleZ = z.enum(["table", "stack"]).catch("table");
export const TabZ = z.enum(["active", "pending", "inactive"]).catch("active");

export type StyleT = z.infer<typeof StyleZ>;
export type TabT = z.infer<typeof TabZ>;

export const SortingZ = z.array(
    z.object({
        desc: z.boolean(),
        id: z.string(),
    })
);

const defaultPageSize = DEFAULT_PATIENT_PAGE_SIZE;

export const PaginationZ = z.object({
    pageSize: z.preprocess(parsePatientPageSize, z.number()),
    pageIndex: z.number().default(0),
});

export type PaginationT = z.infer<typeof PaginationZ>;
export const patientsSchemaZ = z
    .object({
        columns: z.string().catch("").optional(),
        style: StyleZ.optional(),
        tab: TabZ.catch("active"),
        // "mine" is legacy saved settings; diaryApi and TableMenu use "direct" for the same meaning.
        diaries: z
            .enum(["direct", "all", "mine"])
            .catch("direct")
            .optional()
            .transform((v) => (v === "mine" ? "direct" : v)),
        organisations: z.array(z.string()).catch(["all"]).optional(),
        diary_access: z.array(z.string().uuid()).optional(),
        diary_types: z.array(z.string()).optional(),
        diary_statuses: z.array(DiaryStatusZ).optional(),
        labels: z.array(z.string()).optional(),
        pagination: PaginationZ.catch({
            pageIndex: 0,
            pageSize: defaultPageSize,
        }),
        sorting: SortingZ.optional(),
        refetcher: z.number().default(0),
    })
    .partial()
    .catch({
        tab: "active",
        pagination: { pageIndex: 0, pageSize: defaultPageSize },
        sorting: [{ id: "start_date", desc: true }],
        refetcher: 1,
    });

export type PatientsSearchT = z.infer<typeof patientsSchemaZ>;

export const Route = createFileRoute("/_app/patients")({
    component: PatientsPageContent,
    errorComponent: PatientsErrorPage,
    validateSearch: (search) => {
        const defaultSearch = {
            tab: "active",
            pagination: { pageIndex: 0, pageSize: defaultPageSize },
            sorting: [{ id: "start_date", desc: true }],
        } as const;
        return patientsSchemaZ.parse({ ...defaultSearch, ...search });
    },
    // Guard the list only. Single patient pages under /patients/$diaryId are linked from elsewhere (worklist, studies).
    beforeLoad: async ({ context, location }) => {
        if (location.pathname.replace(/\/$/, "") === "/patients") await requireTab(context, "patients");
    },
    loaderDeps: ({ search }) => ({ search }),
    loader: async ({ context, deps }) => {
        if (!context?.queryClient || !context.auth?.id) {
            throw new Error("Unauthorized access. Please log in.");
        }

        const patientsSettings = await context.queryClient.ensureQueryData(
            selectPatientsSettingsByIdQueryOptions(context.auth.id)
        );

        const saved = (patientsSettings?.patients_settings ?? {}) as PatientsSearchT;
        const fromUrl = deps.search;
        const settings = {
            ...saved,
            ...fromUrl,
            // Prefer saved diaries when not in URL; default "direct" when neither has it
            diaries: fromUrl.diaries ?? saved.diaries ?? "direct",
        };

        if (settings.tab) {
            // Prefetch diary data without blocking navigation.
            // On first load this kicks off the fetch; on sort/page changes it
            // avoids blocking so keepPreviousData can show old rows while new
            // data loads in the background.
            context.queryClient.prefetchQuery(diariesBySettingsQueryOptions(settings, context.auth));
        }

        const profile = await context.queryClient.ensureQueryData(selectProfileByIdQueryOptions(context.auth.id));

        return { settings, profile };
    },
});
